import { useState } from 'react';
import { useLocation, NavLink } from 'react-router-dom';
import { useAdminMessaging } from '@/contexts/AdminMessagingContext';
import { useAdminAuth } from '@/contexts/useAdminAuth';
import { canAccessAdminPage } from '@/lib/admin-access';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { cn } from '@/lib/utils';
import {
  Sidebar,
  SidebarBody,
  SidebarFooter,
  SidebarHeader,
  SidebarHeading,
  SidebarLabel,
  SidebarSection,
} from '@a7y/tailwind-catalyst';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import {
  AcademicCapIcon,
  ArrowPathIcon,
  ArrowTrendingUpIcon,
  BanknotesIcon,
  BeakerIcon,
  BoltIcon,
  BuildingOffice2Icon,
  ChartBarSquareIcon,
  ChatBubbleLeftRightIcon,
  ClockIcon,
  Cog8ToothIcon,
  CreditCardIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  FlagIcon,
  HeartIcon,
  MegaphoneIcon,
  NoSymbolIcon,
  RocketLaunchIcon,
  ScaleIcon,
  ShieldCheckIcon,
  ShieldExclamationIcon,
  SignalIcon,
  Squares2X2Icon,
  TicketIcon,
  UserGroupIcon,
  UsersIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/20/solid';
import { UserButton } from '@clerk/clerk-react';

const dashboardNav = [
  { name: 'Dashboard', href: '/admin', icon: Squares2X2Icon, exact: true },
] as const;

const userOpsNav = [
  { name: 'Organizations', href: '/admin/organizations', icon: BuildingOffice2Icon },
  { name: 'Users', href: '/admin/users', icon: UsersIcon },
  { name: 'Subscriptions', href: '/admin/subscriptions', icon: CreditCardIcon },
  { name: 'Promo Codes', href: '/admin/promo-codes', icon: TicketIcon },
  { name: 'Product Emails', href: '/admin/product-emails', icon: EnvelopeIcon },
  { name: 'Email Suppressions', href: '/admin/email-suppressions', icon: NoSymbolIcon },
  { name: 'Trial Eligibility', href: '/admin/trial-eligibility', icon: ShieldCheckIcon },
  { name: 'Announcements', href: '/admin/announcements', icon: MegaphoneIcon },
  { name: 'Release Notes', href: '/admin/release-notes', icon: RocketLaunchIcon },
  { name: 'Learning Topics', href: '/admin/learning-topics', icon: AcademicCapIcon },
  { name: 'Support Messages', href: '/admin/messaging', icon: ChatBubbleLeftRightIcon },
] as const;

const affiliateNav = [
  { name: 'Program Overview', href: '/admin/affiliates/overview', icon: ChartBarSquareIcon },
  { name: 'Affiliates', href: '/admin/affiliates', icon: UserGroupIcon, exact: true },
  { name: 'Campaigns', href: '/admin/affiliates/campaigns', icon: MegaphoneIcon },
  { name: 'Commissions', href: '/admin/affiliates/commissions', icon: CurrencyDollarIcon },
  { name: 'Payouts', href: '/admin/affiliates/payouts', icon: BanknotesIcon },
  { name: 'Suspicious Queue', href: '/admin/affiliates/suspicious', icon: ShieldExclamationIcon },
  { name: 'First-Commission Holds', href: '/admin/affiliates/first-hold', icon: ClockIcon },
  { name: 'Reconciliation', href: '/admin/affiliates/reconciliation', icon: ScaleIcon },
] as const;

const financeNav = [
  { name: 'P&L', href: '/admin/finance/pnl', icon: ArrowTrendingUpIcon },
  { name: 'Audit Spend', href: '/admin/audit-spend', icon: CurrencyDollarIcon },
  { name: 'SaaS Spend', href: '/admin/saas-spend', icon: CurrencyDollarIcon },
] as const;

import {
  addisNav,
  aiApisNav,
  aphidNav,
  cmoNav,
  contentNav,
  isNavItemActive,
  sectionContainsActiveRoute,
  statsNav,
  ukkoNav,
  type NavItemDef,
} from './admin-nav';

const platformHealthNav = [
  { name: 'Overview', href: '/admin/platform-health', icon: HeartIcon, exact: true },
  { name: 'Findings', href: '/admin/platform-health/critical', icon: ShieldExclamationIcon },
  { name: 'Staging Deploys', href: '/admin/platform-health/staging-deploys', icon: BeakerIcon },
  { name: 'Prod Deploys', href: '/admin/platform-health/prod-deploys', icon: RocketLaunchIcon },
  { name: 'Runs', href: '/admin/platform-health/test-runs', icon: SignalIcon },
  { name: 'PULSE', href: '/admin/platform-health/pulse', icon: BoltIcon },
  { name: 'MANTIS', href: '/admin/platform-health/mantis', icon: ArrowPathIcon },
  { name: 'AQUIX', href: '/admin/platform-health/aquix', icon: EyeIcon },
  {
    name: 'Audit Assertions',
    href: '/admin/platform-health/audit-assertions',
    icon: ShieldCheckIcon,
  },
  { name: 'PRISM', href: '/admin/platform-health/prism', icon: EyeIcon },
  {
    name: 'Credibility & Drift',
    href: '/admin/platform-health/credibility',
    icon: ChartBarSquareIcon,
  },
  { name: 'State Invariants', href: '/admin/platform-health/state-invariants', icon: ScaleIcon },
  { name: 'Email Delivery', href: '/admin/platform-health/email-delivery', icon: EnvelopeIcon },
] as const;

const featureHarnessNav = [
  { name: 'Audit Testing', href: '/admin/audit-testing', icon: BeakerIcon },
  { name: 'Quick App Search', href: '/admin/utils', icon: WrenchScrewdriverIcon },
] as const;

const sysOpsNav = [
  { name: 'Errors & Warnings', href: '/admin/errors-warnings', icon: ExclamationTriangleIcon },
  { name: 'Feature Flags', href: '/admin/feature-flags', icon: FlagIcon },
  { name: 'Service Health', href: '/admin/service-health', icon: SignalIcon },
  { name: 'Billing', href: '/admin/billing', icon: CreditCardIcon, v2Only: true },
  { name: 'System', href: '/admin/system', icon: Cog8ToothIcon, v2Only: true },
  { name: 'Audit Logs', href: '/admin/audit-logs', icon: DocumentTextIcon, v2Only: true },
] as const;

/**
 * Section heading matched to the webapp project nav (`SidebarNav.tsx`): the
 * `!` overrides beat Catalyst's own `mb-1` / `text-zinc-500` defaults, which
 * would otherwise win on utility ordering. `text-zinc-400` is forced rather
 * than left to Catalyst's `dark:` variant because the admin sidebar is dark
 * regardless of the app-level theme class.
 */
const SECTION_HEADING_CLASS = '!mb-0 !p-0 !text-zinc-400';

/** Persists which sections the user has manually collapsed across reloads. */
const COLLAPSED_SECTIONS_STORAGE_KEY = 'a7y-admin-nav-collapsed-sections';

/** Section keys, all closed by default until the user expands one. */
const DEFAULT_COLLAPSED_SECTIONS: Record<string, boolean> = {
  'user-ops': true,
  affiliates: true,
  finance: true,
  'ai-apis': true,
  'feature-harnesses': true,
  ukko: true,
  addis: true,
  aphid: true,
  cmo: false,
  content: true,
  'platform-health': true,
  'dev-ops': true,
};

function loadCollapsedSections(): Record<string, boolean> {
  try {
    const raw = window.localStorage.getItem(COLLAPSED_SECTIONS_STORAGE_KEY);
    return raw ? { ...DEFAULT_COLLAPSED_SECTIONS, ...JSON.parse(raw) } : DEFAULT_COLLAPSED_SECTIONS;
  } catch {
    return DEFAULT_COLLAPSED_SECTIONS;
  }
}

function saveCollapsedSections(collapsed: Record<string, boolean>) {
  try {
    window.localStorage.setItem(COLLAPSED_SECTIONS_STORAGE_KEY, JSON.stringify(collapsed));
  } catch {
    // localStorage unavailable (private browsing, quota) — collapse state just won't persist.
  }
}

/**
 * Clickable section header mirroring the webapp project nav's
 * `SidebarGroup` chevron treatment (`SidebarNav.tsx`'s `SidebarGroup`) —
 * rotates 180° when expanded, toggles the section's children below it.
 */
function CollapsibleNavHeading({
  title,
  isExpanded,
  onToggle,
}: {
  title: string;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={isExpanded}
      data-testid={`admin-nav-section-toggle-${title.toLowerCase().replace(/\s+/g, '-')}`}
      // `mt-1` rather than `mt-4` (TPF, 2026-09-09): with ten sections, most of
      // them collapsed most of the time, 16px between headings turned the nav
      // into a scroll. `py-1` stays — it is the button's hit target, not
      // decoration, and the 24px line box plus 8px is already the floor for a
      // comfortable click.
      className="!mt-1 flex w-full cursor-pointer items-center justify-between rounded-lg px-2 py-1 text-left hover:bg-white/5"
    >
      <SidebarHeading className={SECTION_HEADING_CLASS}>{title}</SidebarHeading>
      <ChevronDownIcon
        aria-hidden="true"
        className={cn(
          'size-3.5 shrink-0 text-zinc-500 transition-transform duration-150',
          isExpanded && 'rotate-180'
        )}
      />
    </button>
  );
}

function NavItem({ item, badge }: { item: NavItemDef; badge?: number }) {
  const { pathname } = useLocation();
  // An entry that spans several route prefixes (A7Y-5210: HELM) is active on any of them, not only on its own `href`.
  const spans = item.activePrefixes !== undefined;
  return (
    <NavLink
      to={item.href}
      end={'exact' in item && item.exact}
      className={({ isActive: own }) => {
        const isActive = spans ? isNavItemActive(item, pathname) : own;
        return cn(
          // Density metrics lifted from the webapp project nav: Catalyst's
          // `px-2` gutter and `text-sm/5`, with the `py-1.5` row height the
          // webapp applies via its `[&>span>a]:!py-1.5` override.
          'group relative flex w-full items-center gap-3 rounded-lg px-2 py-1.5 text-sm/5 font-medium transition-colors',
          isActive ? 'text-white' : 'text-zinc-300 hover:bg-white/5 hover:text-white'
        );
      }}
    >
      {({ isActive: own }) => {
        const isActive = spans ? isNavItemActive(item, pathname) : own;
        return (
          <>
            {/* Active rail, same treatment as Catalyst's `SidebarItem`. The
              `-left-4` offset lands it on the sidebar edge, cancelling
              `SidebarBody`'s `p-4` gutter. */}
            {isActive && (
              <span
                aria-hidden="true"
                className="absolute inset-y-1 -left-4 w-0.5 rounded-full bg-white"
              />
            )}
            <item.icon
              aria-hidden="true"
              className={cn(
                'size-4 shrink-0 transition-colors',
                isActive ? 'fill-white' : 'fill-zinc-500 group-hover:fill-white'
              )}
            />
            <SidebarLabel>{item.name}</SidebarLabel>
            {badge != null && badge > 0 && (
              <span className="ml-auto flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
                {badge}
              </span>
            )}
          </>
        );
      }}
    </NavLink>
  );
}
/**
 * One collapsible section: a clickable heading (chevron rotates, matching
 * the webapp project nav's `SidebarGroup` treatment) plus its nav items.
 * Collapse state is user-toggled and persisted to localStorage, but a
 * section auto-expands whenever the active route lives inside it — a
 * collapsed section can never hide the page you're currently on.
 */
function NavSection({
  title,
  items,
  isCollapsed,
  onToggle,
  pathname,
  badgeFor,
}: {
  title: string;
  items: readonly NavItemDef[];
  isCollapsed: boolean;
  onToggle: () => void;
  pathname: string;
  badgeFor?: (name: string) => number | undefined;
}) {
  const isExpanded = !isCollapsed || sectionContainsActiveRoute(items, pathname);
  // A7Y-4957: a section the caller's role has no page in is not rendered.
  if (items.length === 0) return null;
  return (
    <>
      <CollapsibleNavHeading title={title} isExpanded={isExpanded} onToggle={onToggle} />
      {isExpanded &&
        items.map((item) => <NavItem key={item.href} item={item} badge={badgeFor?.(item.name)} />)}
    </>
  );
}

export function AdminSidebar() {
  const { unreadThreadCount } = useAdminMessaging();
  const { role } = useAdminAuth();
  const showV2 = useFeatureFlag('V2');
  const location = useLocation();
  const [collapsedSections, setCollapsedSections] =
    useState<Record<string, boolean>>(loadCollapsedSections);

  const toggleSection = (key: string) => {
    setCollapsedSections((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      saveCollapsedSections(next);
      return next;
    });
  };

  // A7Y-4957: every section shows only the pages the caller's role can open.
  const forRole = (items: readonly NavItemDef[]) =>
    items.filter((item) => canAccessAdminPage(role, item.href));
  const visibleSysOpsNav = sysOpsNav.filter((item) => !('v2Only' in item && item.v2Only) || showV2);

  return (
    <Sidebar className="bg-zinc-950 text-white">
      <SidebarHeader className="border-b-0 pb-0">
        <h1 className="px-2 text-base font-semibold tracking-tight text-white">Apptonomy Admin</h1>
      </SidebarHeader>
      {/* One section for the whole tree — grouping with separate
          `SidebarSection`s would reintroduce Catalyst's `mt-8` inter-section
          gap. Headings carry their own (much tighter) `mt-1` instead, which is
          how the webapp project nav is built. */}
      <SidebarBody className="pt-3">
        <SidebarSection className="!gap-0">
          {forRole(dashboardNav).map((item) => (
            <NavItem key={item.name} item={item} />
          ))}

          <NavSection
            title="CMO"
            items={forRole(cmoNav)}
            isCollapsed={!!collapsedSections.cmo}
            onToggle={() => toggleSection('cmo')}
            pathname={location.pathname}
          />

          <NavSection
            title="Content"
            items={forRole(contentNav)}
            isCollapsed={!!collapsedSections.content}
            onToggle={() => toggleSection('content')}
            pathname={location.pathname}
          />

          <NavSection
            title="User Ops"
            items={forRole(userOpsNav)}
            isCollapsed={!!collapsedSections['user-ops']}
            onToggle={() => toggleSection('user-ops')}
            pathname={location.pathname}
            badgeFor={(name) => (name === 'Support Messages' ? unreadThreadCount : undefined)}
          />

          <NavSection
            title="Affiliates"
            items={forRole(affiliateNav)}
            isCollapsed={!!collapsedSections['affiliates']}
            onToggle={() => toggleSection('affiliates')}
            pathname={location.pathname}
          />

          <NavSection
            title="Finance"
            items={forRole(financeNav)}
            isCollapsed={!!collapsedSections['finance']}
            onToggle={() => toggleSection('finance')}
            pathname={location.pathname}
          />

          <NavSection
            title="AI APIs"
            items={forRole(aiApisNav)}
            isCollapsed={!!collapsedSections['ai-apis']}
            onToggle={() => toggleSection('ai-apis')}
            pathname={location.pathname}
          />

          <NavSection
            title="Stats"
            items={forRole(statsNav)}
            isCollapsed={!!collapsedSections.stats}
            onToggle={() => toggleSection('stats')}
            pathname={location.pathname}
          />

          <NavSection
            title="Feature Harnesses"
            items={forRole(featureHarnessNav)}
            isCollapsed={!!collapsedSections['feature-harnesses']}
            onToggle={() => toggleSection('feature-harnesses')}
            pathname={location.pathname}
          />

          <NavSection
            title="Ukko"
            items={forRole(ukkoNav)}
            isCollapsed={!!collapsedSections.ukko}
            onToggle={() => toggleSection('ukko')}
            pathname={location.pathname}
          />

          <NavSection
            title="ADDIS"
            items={forRole(addisNav)}
            isCollapsed={!!collapsedSections.addis}
            onToggle={() => toggleSection('addis')}
            pathname={location.pathname}
          />

          <NavSection
            title="APHID"
            items={forRole(aphidNav)}
            isCollapsed={!!collapsedSections.aphid}
            onToggle={() => toggleSection('aphid')}
            pathname={location.pathname}
          />

          <NavSection
            title="Platform Health"
            items={forRole(platformHealthNav)}
            isCollapsed={!!collapsedSections['platform-health']}
            onToggle={() => toggleSection('platform-health')}
            pathname={location.pathname}
          />

          <NavSection
            title="Dev Ops"
            items={forRole(visibleSysOpsNav)}
            isCollapsed={!!collapsedSections['dev-ops']}
            onToggle={() => toggleSection('dev-ops')}
            pathname={location.pathname}
          />
        </SidebarSection>
      </SidebarBody>
      {/* Clerk's own control: avatar + name, click opens Clerk's menu, which
          carries "Manage account" and "Sign out". Sign-out runs Clerk's
          `signOut()` — the same call the retired local `logout()` wrapped —
          and `AdminAuthProvider` clears its admin state off the resulting
          null Clerk user, so no separate handler is needed. */}
      <SidebarFooter className="!p-3">
        <UserButton
          showName
          appearance={{
            elements: {
              rootBox: 'w-full',
              // Clerk orders the trigger [name][avatar]; reversing it puts the
              // avatar first so the footer reads with the same icon-then-label
              // rhythm as the nav rows above.
              userButtonBox: 'w-full flex-row-reverse justify-end gap-3',
              userButtonTrigger: 'w-full rounded-lg px-2 py-1.5 transition-colors hover:bg-white/5',
              userButtonAvatarBox: 'size-6',
              userButtonOuterIdentifier: 'truncate text-sm/5 font-medium text-white',
            },
          }}
        />
      </SidebarFooter>
    </Sidebar>
  );
}
